import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  CodeBlock,
  CodeBlockCode,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
} from '@patternfly/react-core';
import { KeyIcon, PencilAltIcon, TrashIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import PubkeyDeleteModal from './PubkeyDeleteModal';
import { useApiFetch } from '../../../api';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { Pubkey } from '../../../types/models/Pubkey';
import { ApiResponsePubkey } from '../../../types/api/ApiResponsePubkey';

const PubkeyDetail: React.FunctionComponent = () => {
  const { pubkeyId } = useParams<{ pubkeyId: string }>();
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const currentUser = useUserContext();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [pubkey, setPubkey] = useState<Pubkey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchPubkey = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/pubkeys/${pubkeyId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponsePubkey;
      if (data.status === 'success' && data.data?.pubkey) {
        setPubkey(data.data.pubkey);
      } else {
        throw new Error('Public key not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load public key'));
    } finally {
      setLoading(false);
    }
  }, [pubkeyId, apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchPubkey();
    }
  }, [auth.isAuthenticated, fetchPubkey]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await apiFetch(`/datacenters/pubkeys/${pubkeyId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      addNotification({
        title: 'Public key deleted',
        srTitle: 'Success alert',
        description: `Public key "${pubkey?.label}" has been deleted.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      navigate('/datacenter/pubkeys');
    } catch (err) {
      addNotification({
        title: 'Error deleting public key',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  if (loading) {
    return (
      <>
        <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
          <Breadcrumb ouiaId="BasicBreadcrumb">
            <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
            <BreadcrumbItem to="/datacenter/pubkeys">Public Keys</BreadcrumbItem>
            <BreadcrumbItem isActive>...</BreadcrumbItem>
          </Breadcrumb>
        </PageBreadcrumb>
        <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
          <Content component="h1">
            <Icon size="lg" style={{ marginRight: '8px' }}>
              <KeyIcon />
            </Icon>
            Public Key
          </Content>
        </PageSection>
        <PageSection hasBodyWrapper={false}>
          <CenteredSpinner />
        </PageSection>
      </>
    );
  }

  if (error || !pubkey) {
    return (
      <>
        <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
          <Breadcrumb ouiaId="BasicBreadcrumb">
            <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
            <BreadcrumbItem to="/datacenter/pubkeys">Public Keys</BreadcrumbItem>
            <BreadcrumbItem isActive>Not found</BreadcrumbItem>
          </Breadcrumb>
        </PageBreadcrumb>
        <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
          <Content component="h1">
            <Icon size="lg" style={{ marginRight: '8px' }}>
              <KeyIcon />
            </Icon>
            Public Key
          </Content>
        </PageSection>
        <PageSection hasBodyWrapper={false}>
          <Content component="p" className="pf-v6-u-text-align-center pf-v6-u-mt-xl">
            {error ? `Error loading public key: ${error.message}` : 'Public key not found.'}
          </Content>
        </PageSection>
      </>
    );
  }

  const isOwner = currentUser !== null && pubkey.userID === currentUser.id;

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/pubkeys">Public Keys</BreadcrumbItem>
          <BreadcrumbItem isActive>{pubkey.label}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <KeyIcon />
              </Icon>
              {pubkey.label}
            </Content>
            <Content component="p">Public key details</Content>
          </FlexItem>
          {isOwner && (
            <FlexItem>
              <Flex spaceItems={{ default: 'spaceItemsSm' }}>
                <FlexItem>
                  <Button
                    variant="primary"
                    icon={<PencilAltIcon />}
                    component={(props) => <Link {...props} to={`/datacenter/pubkeys/${pubkeyId}/edit`} />}
                  >
                    Edit
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Button variant="danger" icon={<TrashIcon />} onClick={() => setIsDeleteModalOpen(true)}>
                    Delete
                  </Button>
                </FlexItem>
              </Flex>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <DescriptionList>
          <DescriptionListGroup>
            <DescriptionListTerm>Label</DescriptionListTerm>
            <DescriptionListDescription>{pubkey.label}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>SSH Public Key</DescriptionListTerm>
            <DescriptionListDescription>
              <CodeBlock>
                <CodeBlockCode>{pubkey.content}</CodeBlockCode>
              </CodeBlock>
            </DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>
      </PageSection>

      <PubkeyDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        pubkeyLabel={pubkey.label}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default PubkeyDetail;
